import { useState, useEffect, useRef, useCallback, type FormEvent, type ChangeEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getConversations, getConversation, getLeadApplication, updateLeadApplication, sendLeadApplicationToSgp, assignConversation, sendMessage, sendMedia, sendDocumentUrl, sendTyping, closeConversation, transferConversation, addNote, getMessageFileUrl, type LeadApplication } from '../api/conversations';
import { getCustomerSgpClient360 } from '../api/customers';
import { getDepartments } from '../api/departments';
import { createTicket } from '../api/tickets';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../hooks/useSocket';
import { Topbar } from '../layout/Topbar';
import { SgpOperationsPanel } from '../components/SgpOperationsPanel';
import { getSgpPreRegistrationOptions } from '../api/sgp';
import type { Conversation, Department, Message } from '../types';

// ── Helpers ──────────────────────────────────────────────────────────────

function getInitials(name: string | null): string {
  if (!name) return '?';
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

function avatarColor(name: string | null): string {
  if (!name) return '#90a4ae';
  const colors = ['#00897b', '#1565c0', '#f57c00', '#2e7d32', '#6a1b9a'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function formatTime(date: string): string {
  const d = new Date(date);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  if (diff < 60000) return 'agora';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}min`;
  if (diff < 86400000) return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function statusLabel(status: string): { label: string; className: string } {
  const map: Record<string, { label: string; className: string }> = {
    BOT: { label: 'Bot', className: 'neutral' },
    WAITING_AGENT: { label: 'Fila', className: 'warning' },
    ASSIGNED: { label: 'Ativo', className: 'primary' },
    CLOSING: { label: 'Encerrando', className: 'danger' },
    CLOSED: { label: 'Fechado', className: 'neutral' },
  };
  return map[status] || { label: status, className: 'neutral' };
}

function contentTypeIcon(type: string): string {
  const icons: Record<string, string> = {
    IMAGE: '📷', AUDIO: '🔊', VIDEO: '🎬', DOCUMENT: '📎',
    TEXT: '💬', SYSTEM: '⚙️', CALLBACK: '🔗',
  };
  return icons[type] || '📎';
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function deliveryIcon(status: string | undefined): string {
  if (!status) return '◌';
  const icons: Record<string, string> = {
    SENT: '✓', DELIVERED: '✓✓', READ: '✓✓', ERROR: '✗', FAILED: '✗',
  };
  return icons[status] || '◌';
}

function deliveryClass(status: string | undefined): string {
  if (!status) return 'pending';
  return status === 'ERROR' || status === 'FAILED' ? 'error' : 'delivered';
}

// ── InboxPage ────────────────────────────────────────────────────────────

export function InboxPage() {
  const { user } = useAuth();
  const { on } = useSocket();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [noteText, setNoteText] = useState('');
  const [noteList, setNoteList] = useState<Array<{ id: string; content: string; author: string; createdAt: string }>>([]);
  const [queueTab, setQueueTab] = useState<'ACTION' | 'MINE' | 'WAITING' | 'AUTOMATIC' | 'RESOLVED'>('ACTION');
  const [searchParams, setSearchParams] = useSearchParams();
  const [uploading, setUploading] = useState(false);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [urlCaption, setUrlCaption] = useState('');
  const [urlFileName, setUrlFileName] = useState('');

  // ── Delivery status state ──
  const [deliveryStatus, setDeliveryStatus] = useState<Record<string, string>>({});

  // ── SGP 360 state ──
  const [sgp360, setSgp360] = useState<Record<string, unknown> | null>(null);
  const [sgpLoading, setSgpLoading] = useState(false);
  const [showSgpModal, setShowSgpModal] = useState(false);
  const [invoiceFilter, setInvoiceFilter] = useState<'pending' | 'paid'>('pending');

  // ── SGP Operations Panel state ──
  const [showSgpPanel, setShowSgpPanel] = useState(false);
  const [leadApplication, setLeadApplication] = useState<LeadApplication | null>(null);
  const [leadForm, setLeadForm] = useState<LeadApplication['data'] | null>(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadSaving, setLeadSaving] = useState(false);
  const [leadSending, setLeadSending] = useState(false);
  const [sgpLeadOptions, setSgpLeadOptions] = useState<{ dueDates: Array<{ id: string; label: string }>; sellers: Array<{ id: string; label: string }>; billingMethods: Array<{ id: string; label: string }> } | null>(null);
  const [sgpLeadForm, setSgpLeadForm] = useState({ dueDateId: '', billingMethodId: '1', sellerId: '', birthDate: '' });

  // ── All departments for transfer ──
  const [allDepartments, setAllDepartments] = useState<Department[]>([]);
  useEffect(() => {
    getDepartments().then(setAllDepartments).catch(() => {});
  }, []);

  // ── Ticket creation state ──
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketDept, setTicketDept] = useState('');
  const [ticketCreating, setTicketCreating] = useState(false);

  // ── Typing state ──
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [customerTyping, setCustomerTyping] = useState(false);

  const messageEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replyInputRef = useRef<HTMLInputElement>(null);

  const selected = conversations.find((c) => c.id === selectedId);

  useEffect(() => {
    const conversationId = searchParams.get('conversation');
    if (!conversationId || !conversations.some((conversation) => conversation.id === conversationId)) return;
    setQueueTab('MINE');
    setSelectedId(conversationId);
    searchParams.delete('conversation');
    setSearchParams(searchParams, { replace: true });
  }, [conversations, searchParams, setSearchParams]);

  useEffect(() => {
    setLeadApplication(null);
    setLeadForm(null);
    if (!selectedId) return;
    getLeadApplication(selectedId).then((application) => {
      if (!application.available) return;
      setLeadApplication(application);
      setLeadForm(application.data);
    }).catch(() => {});
  }, [selectedId, selected?.customer?.updatedAt]);

  const handleSaveLead = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedId || !leadForm) return;
    setLeadSaving(true);
    try {
      const application = await updateLeadApplication(selectedId, leadForm);
      setLeadApplication(application);
      setLeadForm(application.data);
      const refreshed = await getConversation(selectedId);
      setConversations((current) => current.map((item) => item.id === selectedId ? refreshed : item));
      setShowLeadModal(false);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Não foi possível salvar o pré-cadastro.');
    } finally {
      setLeadSaving(false);
    }
  };

  useEffect(() => {
    if (!showLeadModal || leadApplication?.status !== 'REVIEWED' || !selected) return;
    getSgpPreRegistrationOptions(selected.companyId).then((options) => {
      setSgpLeadOptions(options);
      setSgpLeadForm((current) => ({
        ...current,
        dueDateId: current.dueDateId || options.dueDates[0]?.id || '',
        billingMethodId: current.billingMethodId || options.billingMethods[0]?.id || '1',
      }));
    }).catch(() => setSgpLeadOptions({ dueDates: [], sellers: [], billingMethods: [] }));
  }, [showLeadModal, leadApplication?.status, selected?.id]);

  const handleSendLeadToSgp = async () => {
    if (!selectedId || !sgpLeadForm.dueDateId || !sgpLeadForm.billingMethodId) return;
    if (!confirm('Confirma o envio definitivo deste pré-cadastro ao SGP? A operação será registrada na auditoria.')) return;
    setLeadSending(true);
    try {
      const response = await sendLeadApplicationToSgp(selectedId, {
        ...sgpLeadForm,
        sellerId: sgpLeadForm.sellerId || undefined,
        birthDate: sgpLeadForm.birthDate || undefined,
      });
      setLeadApplication(response.application);
      setShowLeadModal(false);
      alert('Pré-cadastro enviado ao SGP com sucesso.');
    } catch (error) {
      alert(error instanceof Error ? error.message : 'O SGP recusou o pré-cadastro.');
    } finally {
      setLeadSending(false);
    }
  };

  // ── Auto-load SGP 360 when customer has SGP data ──
  const sgpAutoLoadedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selected?.customer) return;
    if (selected.id === sgpAutoLoadedRef.current) return;
    if (selected.customer.externalProvider === 'SGP' && selected.customer.document) {
      sgpAutoLoadedRef.current = selected.id;
      setSgpLoading(true);
      getCustomerSgpClient360(selected.customer.id)
        .then((data) => { if (data?.available !== false) setSgp360(data); })
        .catch(() => {})
        .finally(() => setSgpLoading(false));
    }
  }, [selected?.id]);

  // ── Load conversations ──
  const loadConversations = useCallback(async () => {
    try {
      const data = await getConversations();
      setConversations(data);
    } catch (err) {
      console.error('Falha ao carregar conversas:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // ── Socket events ──
  useEffect(() => {
    const unsubConnect = on('connect', () => { void loadConversations(); });
    const unsub1 = on('conversation.created', (conv: any) => {
      setConversations((prev: Conversation[]) => {
        if (prev.find((c) => c.id === conv.id)) return prev;
        return [conv, ...prev];
      });
    });
    const unsub2 = on('conversation.status_changed', (conv: any) => {
      setConversations((prev: Conversation[]) => prev.map((c) => (c.id === conv.id ? { ...c, ...conv } : c)));
    });
    const unsub3 = on('message.created', (msg: any) => {
      setConversations((prev: Conversation[]) => {
        if (!prev.some((c) => c.id === msg.conversationId)) {
          getConversation(msg.conversationId)
            .then((conversation) => setConversations((current) => current.some((item) => item.id === conversation.id) ? current : [conversation, ...current]))
            .catch(() => {});
        }
        return prev.map((c) =>
          c.id === msg.conversationId && !(c.messages || []).some((m) => m.id === msg.id)
            ? { ...c, messages: [...(c.messages || []), msg], lastMessageAt: msg.createdAt }
            : c,
        );
      });
    });
    const unsub4 = on('message.delivery_changed', (evt: any) => {
      const msgId = evt.storedMessageId || evt.messageId;
      if (msgId) {
        setDeliveryStatus((prev) => ({ ...prev, [msgId]: evt.status }));
      }
    });
    const unsub5 = on('sales.lead_ready', (evt: any) => {
      if (!evt?.conversationId) return;
      getConversation(evt.conversationId)
        .then((conversation) => {
          setConversations((current) => current.some((item) => item.id === conversation.id)
            ? current.map((item) => item.id === conversation.id ? conversation : item)
            : [conversation, ...current]);
        })
        .catch(() => {});
      if (selectedId === evt.conversationId) {
        getLeadApplication(evt.conversationId).then((application) => {
          if (!application.available) return;
          setLeadApplication(application);
          setLeadForm(application.data);
        }).catch(() => {});
      }
    });
    return () => { unsubConnect(); unsub1(); unsub2(); unsub3(); unsub4(); unsub5(); };
  }, [on, loadConversations, selectedId]);

  // ── Refresh selected conversation from API (fallback for missed socket events) ──
  const lastRefreshRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    getConversation(selectedId)
      .then((full) => {
        if (cancelled) return;
        lastRefreshRef.current = selectedId;
        setConversations((prev) =>
          prev.map((c) => (c.id === full.id ? { ...c, ...full } : c)),
        );
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedId]);

  // ── Periodic poll for new messages (every 10s) ──
  useEffect(() => {
    if (!selectedId) return;
    const interval = setInterval(() => {
      getConversation(selectedId)
        .then((full) => {
          setConversations((prev) =>
            prev.map((c) => (c.id === full.id ? { ...c, ...full } : c)),
          );
        })
        .catch(() => {});
    }, 10000);
    return () => clearInterval(interval);
  }, [selectedId]);

  // ── Scroll on new messages ──
  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selected?.messages]);

  // ── Load notes when conversation changes ──
  const prevSelectedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (selected) {
      const notes = (selected.messages || []).filter((m) => m.contentType === 'SYSTEM' && m.senderType === 'SYSTEM');
      setNoteList(notes.map((n) => ({
        id: n.id,
        content: n.content,
        author: n.senderUser?.name || 'Sistema',
        createdAt: n.createdAt,
      })));
      setCustomerTyping(false);
      if (prevSelectedIdRef.current !== selectedId) {
        setSgp360(null);
        prevSelectedIdRef.current = selectedId;
      }
    }
  }, [selectedId, selected]);

  // ── Filtered queue ──
  const filteredConversations = conversations.filter((c) => {
    if (queueTab === 'MINE') return c.assignedAgentId === user?.sub;
    if (queueTab === 'WAITING') return c.status === 'WAITING_AGENT';
    if (queueTab === 'AUTOMATIC') return c.status === 'BOT';
    if (queueTab === 'RESOLVED') return c.status === 'CLOSED' || c.status === 'CLOSING';
    if (queueTab === 'ACTION') return c.status === 'WAITING_AGENT';
    return true;
  }).sort((a, b) => {
    const aTime = a.lastMessageAt || a.createdAt;
    const bTime = b.lastMessageAt || b.createdAt;
    return new Date(bTime).getTime() - new Date(aTime).getTime();
  });

  // ── Unique departments for transfer ──
  const availableDepartments = conversations
    .map((c) => c.department)
    .filter((d): d is Department => !!d)
    .filter((d, i, arr) => arr.findIndex((a) => a.id === d.id) === i);

  // ── Actions ──

  const handleAssign = async () => {
    if (!selectedId) return;
    try {
      const updated = await assignConversation(selectedId);
      setConversations((prev) => prev.map((c) => (c.id === selectedId ? { ...c, ...updated } : c)));
      setQueueTab('MINE');
    } catch (err) {
      console.error('Falha ao assumir conversa:', err);
    }
  };

  // ── Optimistic message insert ──
  const appendMessage = (sent: Message) => {
    setConversations((prev) =>
      prev.map((c) =>
        c.id === sent.conversationId && !(c.messages || []).some((m) => m.id === sent.id)
          ? { ...c, messages: [...(c.messages || []), sent], lastMessageAt: sent.createdAt }
          : c,
      ),
    );
  };

  const handleSendMessage = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedId || !replyText.trim()) return;
    const text = replyText;
    setReplyText('');
    try {
      const sent = await sendMessage(selectedId, text);
      if (sent) appendMessage(sent);
    } catch (err) {
      console.error('Falha ao enviar mensagem:', err);
    }
  };

  // ── Typing indicator ──
  const handleTyping = (value: string) => {
    setReplyText(value);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      if (selectedId) {
        sendTyping(selectedId).catch(() => {});
      }
    }, 800);
  };

  // ── File upload ──
  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedId) return;
    setUploading(true);
    try {
      const sent = await sendMedia(selectedId, file);
      if (sent) appendMessage(sent);
    } catch (err) {
      console.error('Falha ao enviar arquivo:', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ── Document URL ──
  const handleSendDocumentUrl = async () => {
    if (!selectedId || !urlInput.trim()) return;
    setUploading(true);
    try {
      const sent = await sendDocumentUrl(selectedId, urlInput.trim(), {
        fileName: urlFileName.trim() || undefined,
        caption: urlCaption.trim() || undefined,
      });
      if (sent) appendMessage(sent);
      setShowUrlModal(false);
      setUrlInput('');
      setUrlCaption('');
      setUrlFileName('');
    } catch (err) {
      console.error('Falha ao enviar documento por URL:', err);
    } finally {
      setUploading(false);
    }
  };

  // ── Close conversation ──
  const handleClose = async () => {
    if (!selectedId) return;
    try {
      const updated = await closeConversation(selectedId);
      setConversations((prev) => prev.map((c) => (c.id === selectedId ? { ...c, ...updated } : c)));
      setSelectedId(null);
    } catch (err) {
      console.error('Falha ao encerrar conversa:', err);
    }
  };

  // ── Transfer ──
  const handleTransfer = async (deptId: string) => {
    if (!selectedId) return;
    try {
      const updated = await transferConversation(selectedId, {
        departmentId: deptId,
        reason: 'Transferência pelo atendente',
      });
      setConversations((prev) => prev.map((c) => (c.id === selectedId ? { ...c, ...updated } : c)));
    } catch (err) {
      console.error('Falha ao transferir conversa:', err);
    }
  };

  // ── Add note ──
  const handleAddNote = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedId || !noteText.trim()) return;
    try {
      const msg = await addNote(selectedId, noteText);
      setNoteList((prev) => [...prev, {
        id: msg.id,
        content: noteText,
        author: user?.name || 'Atendente',
        createdAt: msg.createdAt,
      }]);
      setNoteText('');
    } catch (err) {
      console.error('Falha ao registrar nota:', err);
    }
  };

  // ── SGP 360 ──
  const handleLoadSgp360 = async () => {
    if (!selected?.customer) return;
    setSgpLoading(true);
    try {
      const data = await getCustomerSgpClient360(selected.customer.id);
      setSgp360(data);
      setShowSgpModal(true);
    } catch (err) {
      console.error('Falha ao carregar SGP 360:', err);
    } finally {
      setSgpLoading(false);
    }
  };

  const [sendingInvoiceId, setSendingInvoiceId] = useState<string | null>(null);
  const handleSendInvoiceToCustomer = async (invoice: any) => {
    if (!selectedId) return;
    setSendingInvoiceId(invoice.id);
    try {
      if (invoice.paymentUrl) {
        const sent = await sendDocumentUrl(selectedId, invoice.paymentUrl, {
          fileName: `boleto-${invoice.documentNumber || invoice.id}.pdf`,
          caption: `💰 Boleto — R$ ${Number(invoice.amount).toFixed(2)}\nVencimento: ${invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('pt-BR') : '—'}`,
        });
        if (sent) appendMessage(sent);
      } else if (invoice.digitableLine) {
        const msg = [
          '💰 *Segunda via de boleto*',
          '',
          `Valor: *R$ ${Number(invoice.amount).toFixed(2)}*`,
          `Vencimento: *${invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('pt-BR') : '—'}*`,
          '',
          '📄 Linha digitável:',
          invoice.digitableLine,
          '',
          'Para pagamento, copie a linha digitável e efetue o pagamento no banco ou app de sua preferência.',
        ].join('\n');
        const sent = await sendMessage(selectedId, msg);
        if (sent) appendMessage(sent);
      } else {
        const msg = [
          '💰 *Dados do boleto*',
          '',
          `Valor: *R$ ${Number(invoice.amount).toFixed(2)}*`,
          `Vencimento: *${invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('pt-BR') : '—'}*`,
          `Documento: ${invoice.documentNumber || '—'}`,
          '',
          'Entre em contato conosco para obter a segunda via.',
        ].join('\n');
        const sent = await sendMessage(selectedId, msg);
        if (sent) appendMessage(sent);
      }
    } catch (err) {
      console.error('Falha ao enviar boleto:', err);
    } finally {
      setSendingInvoiceId(null);
    }
  };

  // ── Create ticket ──
  const handleCreateTicket = async (e: FormEvent) => {
    e.preventDefault();
    if (!selected || !ticketSubject.trim() || !ticketDept) return;
    setTicketCreating(true);
    try {
      await createTicket({
        customerId: selected.customerId,
        conversationId: selected.id,
        departmentId: ticketDept,
        subject: ticketSubject,
      });
      setShowTicketModal(false);
      setTicketSubject('');
      setTicketDept('');
    } catch {
      console.error('Falha ao criar ticket');
    } finally {
      setTicketCreating(false);
    }
  };

  // ── Render helpers ──

  function renderMediaPreview(msg: { id: string; contentType: string; content: string; metadata: Record<string, unknown> | null }) {
    const fileUrl = getMessageFileUrl(msg.id);
    const attachment = msg.metadata?.attachment as Record<string, unknown> | undefined;
    const fileName = attachment?.fileName as string || msg.content;
    const fileSize = attachment?.fileSize as number | undefined;

    switch (msg.contentType) {
      case 'IMAGE':
        return (
          <div className="media-preview">
            <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="media-thumb-link">
              <img src={fileUrl} alt={fileName} className="media-thumb" loading="lazy" />
            </a>
          </div>
        );
      case 'AUDIO':
        return (
          <div className="media-preview">
            <audio controls src={fileUrl} className="media-audio" preload="metadata">
              Seu navegador não suporta áudio.
            </audio>
          </div>
        );
      case 'VIDEO':
        return (
          <div className="media-preview">
            <video controls src={fileUrl} className="media-video" preload="metadata">
              Seu navegador não suporta vídeo.
            </video>
          </div>
        );
      case 'DOCUMENT':
        return (
          <div className="attachment-card">
            <span className="attachment-icon">📎</span>
            <div className="attachment-info">
              <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="attachment-name">
                {fileName}
              </a>
              <span className="attachment-meta">
                Documento {fileSize ? ` • ${formatFileSize(fileSize)}` : ''}
              </span>
            </div>
          </div>
        );
      default:
        return null;
    }
  }

  function renderDeliveryIndicator(msgId: string) {
    const status = deliveryStatus[msgId];
    if (!status) return null;
    return (
      <span className={`delivery-status ${deliveryClass(status)}`} title={status}>
        {deliveryIcon(status)}
      </span>
    );
  }

  return (
    <div className="view-container" style={{ padding: 0 }}>
      <div style={{ padding: 'var(--space-md) var(--space-lg)', paddingBottom: 0 }}>
        <Topbar title="Atendimento" subtitle="Cockpit de atendimento multicanal." />
      </div>

      <div className="inbox-layout" style={{ padding: 'var(--space-md) var(--space-lg)' }}>
        {/* ═══════ QUEUE PANEL ═══════ */}
        <div className="panel queue-panel">
          <div className="panel-header">
            <h2>Fila de atendimento</h2>
            <span className="text-sm text-secondary">{filteredConversations.length}</span>
          </div>
          <div className="queue-tabs">
            {(['ACTION', 'MINE', 'WAITING', 'AUTOMATIC', 'RESOLVED'] as const).map((tab) => (
              <button
                key={tab}
                className={`queue-tab${queueTab === tab ? ' active' : ''}`}
                onClick={() => setQueueTab(tab)}
                type="button"
              >
                {{ ACTION: 'Ação', MINE: 'Meus', WAITING: 'Fila', AUTOMATIC: 'Automático', RESOLVED: 'Fechados' }[tab]}
                <span className="queue-count">
                  {conversations.filter((c) => {
                    if (tab === 'MINE') return c.assignedAgentId === user?.sub;
                    if (tab === 'WAITING') return c.status === 'WAITING_AGENT';
                    if (tab === 'AUTOMATIC') return c.status === 'BOT';
                    if (tab === 'RESOLVED') return c.status === 'CLOSED' || c.status === 'CLOSING';
                    if (tab === 'ACTION') return c.status === 'WAITING_AGENT';
                    return true;
                  }).length}
                </span>
              </button>
            ))}
          </div>
          <div className="conversation-list">
            {loading ? (
              <div className="empty-state"><div className="spinner" /></div>
            ) : filteredConversations.length === 0 ? (
              <div className="empty-state"><p>Nenhuma conversa encontrada.</p></div>
            ) : (
              filteredConversations.map((conv) => {
                const st = statusLabel(conv.status);
                const lastMsg = conv.messages?.[conv.messages.length - 1];
                return (
                  <div
                    key={conv.id}
                    className={`conversation-item${selectedId === conv.id ? ' active' : ''}`}
                    onClick={() => setSelectedId(conv.id)}
                  >
                    <div className="avatar" style={{ background: avatarColor(conv.customer?.name) }}>
                      {getInitials(conv.customer?.name)}
                      {conv.status === 'WAITING_AGENT' && !conv.assignedAgentId && (
                        <span className="avatar-dot pulse" />
                      )}
                    </div>
                    <div className="content">
                      <div className="name-status">
                        <span className="name">{conv.customer?.name || 'Cliente'}</span>
                        <span className="time">{conv.lastMessageAt ? formatTime(conv.lastMessageAt) : ''}</span>
                      </div>
                      <div className="preview">
                        {lastMsg ? (
                          lastMsg.contentType !== 'TEXT' && lastMsg.contentType !== 'SYSTEM'
                            ? `${contentTypeIcon(lastMsg.contentType)} ${lastMsg.content.slice(0, 80)}`
                            : lastMsg.content.slice(0, 100)
                        ) : 'Sem mensagens'}
                      </div>
                      <div className="badges">
                        <span className={`pill ${st.className}`}>{st.label}</span>
                        {conv.channel === 'WHATSAPP' && <span className="pill neutral">📱 WA</span>}
                        {conv.channel === 'TELEGRAM' && <span className="pill neutral">✈️ TG</span>}
                        {conv.department && <span className="pill neutral">{conv.department.name}</span>}
                        {conv.assignedAgent && (
                          <span className="pill primary" title={conv.assignedAgent.name}>
                            👤 {getInitials(conv.assignedAgent.name)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* ═══════ CHAT PANEL ═══════ */}
        <div className="panel chat-panel">
          {!selected ? (
            <div className="empty-state" style={{ flex: 1 }}>
              <span className="icon" style={{ fontSize: '3rem', opacity: 0.15 }}>💬</span>
              <p>Selecione uma conversa para iniciar o atendimento.</p>
            </div>
          ) : (
            <>
              {/* ── Chat Header ── */}
              <div className="chat-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                  <div className="avatar-sm" style={{ background: avatarColor(selected.customer?.name) }}>
                    {getInitials(selected.customer?.name)}
                  </div>
                  <div>
                    <span className="customer-name">{selected.customer?.name || 'Cliente'}</span>
                    <span className="channel-badge">
                      via {selected.channel === 'WHATSAPP' ? 'WhatsApp' : 'Telegram'}
                      {selected.department ? ` • ${selected.department.name}` : ''}
                      {selected.assignedAgent ? ` • ${selected.assignedAgent.name}` : ''}
                    </span>
                  </div>
                </div>
                <div className="flex gap-sm">
                  <span className={`pill ${statusLabel(selected.status).className}`}>
                    {statusLabel(selected.status).label}
                  </span>
                </div>
              </div>

              {/* ── Message List ── */}
              <div className="message-list">
                {(!selected.messages || selected.messages.length === 0) ? (
                  <div className="empty-state"><p>Nenhuma mensagem nesta conversa.</p></div>
                ) : (
                  selected.messages.map((msg) => {
                    const isMedia = ['IMAGE', 'AUDIO', 'VIDEO', 'DOCUMENT'].includes(msg.contentType);
                    const isOutbound = msg.senderType === 'AGENT' || msg.direction === 'OUTBOUND';
                    return (
                      <div
                        key={msg.id}
                        className={`message-bubble ${
                          msg.senderType === 'CUSTOMER'
                            ? 'inbound'
                            : msg.senderType === 'SYSTEM'
                              ? 'system'
                              : 'outbound'
                        }`}
                      >
                        {msg.senderType === 'AGENT' && msg.senderUser && (
                          <div className="sender-name">{msg.senderUser.name}</div>
                        )}

                        {isMedia ? renderMediaPreview(msg) : (
                          <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                        )}

                        <div className="message-footer">
                          <span className="time">
                            {new Date(msg.createdAt).toLocaleTimeString('pt-BR', {
                              hour: '2-digit', minute: '2-digit',
                            })}
                          </span>
                          {isOutbound && msg.contentType === 'TEXT' && renderDeliveryIndicator(msg.id)}
                        </div>
                      </div>
                    );
                  })
                )}

                {/* ── Customer typing indicator ── */}
                {customerTyping && (
                  <div className="typing-indicator">
                    <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
                    <span className="typing-label">Cliente digitando...</span>
                  </div>
                )}

                <div ref={messageEndRef} />
              </div>

              {/* ── Reply Box ── */}
              <form className="reply-box" onSubmit={handleSendMessage}>
                <input
                  ref={replyInputRef}
                  value={replyText}
                  onChange={(e) => handleTyping(e.target.value)}
                  placeholder="Digite a resposta do atendente..."
                  autoFocus
                  disabled={uploading}
                />

                <div className="attach-actions">
                  <button
                    type="button"
                    className="attach-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    title="Anexar arquivo"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                    </svg>
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    onChange={handleFileSelect}
                    accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  />

                  <button
                    type="button"
                    className="attach-btn"
                    onClick={() => setShowUrlModal(true)}
                    disabled={uploading}
                    title="Enviar documento por URL"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                  </button>
                </div>

                <button type="submit" disabled={!replyText.trim() || uploading} title="Enviar">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                </button>
              </form>

              {uploading && (
                <div className="upload-bar">
                  <span>Enviando anexo...</span>
                  <div className="upload-progress" />
                </div>
              )}
            </>
          )}
        </div>

        {/* ═══════ CUSTOMER PANEL ═══════ */}
        <div className="panel customer-panel">
          {!selected ? (
            <div className="empty-state">
              <span className="icon">👤</span>
              <p>Selecione uma conversa.</p>
            </div>
          ) : (
            <>
              <div className="panel-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                  <div className="avatar" style={{
                    width: 32, height: 32, borderRadius: 'var(--radius-full)',
                    background: avatarColor(selected.customer?.name), color: 'white',
                    fontWeight: 600, fontSize: '0.857rem', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {getInitials(selected.customer?.name)}
                  </div>
                  <h2>{selected.customer?.name || 'Cliente'}</h2>
                </div>
                {selected.customer?.document && (
                  <span className="pill neutral" style={{ fontSize: '0.714rem' }}>
                    {selected.customer.document}
                  </span>
                )}
              </div>

              {/* ── Customer Details ── */}
              <div className="customer-details">
                <div className="detail-section">
                  <span className="detail-section-title">Identificação</span>
                  <div className="detail-row">
                    <span className="label">Documento</span>
                    <span className="value">{selected.customer?.document || '—'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Tipo</span>
                    <span className="value">{selected.customer?.customerType || '—'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Nascimento</span>
                    <span className="value">
                      {selected.customer?.birthDate
                        ? selected.customer.birthDate.slice(0, 10).split('-').reverse().join('/')
                        : '—'}
                    </span>
                  </div>
                  {selected.customer?.isLead && (
                    <div className="detail-row">
                      <span className="label">Lead</span>
                      <span className="value"><span className="pill warning">Lead</span></span>
                    </div>
                  )}
                </div>

                <div className="detail-section">
                  <span className="detail-section-title">Contato</span>
                  <div className="detail-row">
                    <span className="label">Telefone</span>
                    <span className="value">{selected.customer?.phone || '—'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Celular</span>
                    <span className="value">{selected.customer?.mobile || '—'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">E-mail</span>
                    <span className="value" style={{ fontSize: '0.786rem' }}>{selected.customer?.email || '—'}</span>
                  </div>
                </div>

                <div className="detail-section">
                  <span className="detail-section-title">Endereço</span>
                  <div className="detail-row">
                    <span className="label">Logradouro</span>
                    <span className="value">
                      {[selected.customer?.addressStreet, selected.customer?.addressNumber]
                        .filter(Boolean).join(', ') || '—'}
                    </span>
                  </div>
                  {selected.customer?.addressComplement && (
                    <div className="detail-row">
                      <span className="label">Complemento</span>
                      <span className="value">{selected.customer.addressComplement}</span>
                    </div>
                  )}
                  <div className="detail-row">
                    <span className="label">Bairro</span>
                    <span className="value">{selected.customer?.addressDistrict || '—'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Cidade/UF</span>
                    <span className="value">
                      {[selected.customer?.addressCity, selected.customer?.addressState]
                        .filter(Boolean).join('/') || '—'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">CEP</span>
                    <span className="value">{selected.customer?.addressZipCode || '—'}</span>
                  </div>
                  {selected.customer?.addressReference && (
                    <div className="detail-row">
                      <span className="label">Referência</span>
                      <span className="value">{selected.customer.addressReference}</span>
                    </div>
                  )}
                </div>

                <div className="detail-section">
                  <span className="detail-section-title">Atendimento</span>
                  <div className="detail-row">
                    <span className="label">Canal</span>
                    <span className="value">
                      {selected.channel === 'WHATSAPP' ? '📱 WhatsApp' : '✈️ Telegram'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Status</span>
                    <span className="value">
                      <span className={`pill ${statusLabel(selected.status).className}`}>
                        {statusLabel(selected.status).label}
                      </span>
                    </span>
                  </div>
                  {selected.assignedAgent && (
                    <div className="detail-row">
                      <span className="label">Atendente</span>
                      <span className="value">{selected.assignedAgent.name}</span>
                    </div>
                  )}
                  <div className="detail-row">
                    <span className="label">Criado em</span>
                    <span className="value">{new Date(selected.createdAt).toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>
              </div>

              {leadApplication?.available && (
                <div className="lead-application-card">
                  <div className="lead-application-heading">
                    <div>
                      <span className="detail-section-title">Pré-cadastro SGP</span>
                      <strong>{leadApplication.plan.label || 'Plano pendente de revisão'}</strong>
                    </div>
                    <span className={`pill ${leadApplication.status === 'IDENTITY_REVIEW_REQUIRED' ? 'danger' : leadApplication.status === 'REVIEWED' ? 'success' : 'warning'}`}>
                      {leadApplication.status === 'IDENTITY_REVIEW_REQUIRED' ? 'Revisar identidade' : leadApplication.status === 'REVIEWED' ? 'Revisado' : 'Revisão pendente'}
                    </span>
                  </div>
                  <p>Dados coletados pela IA e preenchidos no cadastro. Confira antes de enviar ao SGP.</p>
                  <div className="lead-application-meta">
                    <span>Plano SGP: {leadApplication.plan.sgpPlanId || 'não vinculado'}</span>
                    <span>Envio: {leadApplication.sgp?.status === 'SENT' ? 'concluído' : 'não realizado'}</span>
                  </div>
                  <button type="button" onClick={() => setShowLeadModal(true)}>Revisar pré-cadastro</button>
                </div>
              )}

              {/* ── Notes Section ── */}
              {noteList.length > 0 && (
                <div className="notes-section">
                  <span className="detail-section-title" style={{ padding: 'var(--space-sm) var(--space-md)' }}>
                    Notas internas ({noteList.length})
                  </span>
                  <div className="notes-list">
                    {noteList.slice(-5).reverse().map((note) => (
                      <div key={note.id} className="note-item">
                        <div className="note-header">
                          <strong>{note.author}</strong>
                          <span className="note-time">{formatTime(note.createdAt)}</span>
                        </div>
                        <p className="note-content">{note.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Action Stack ── */}
              {selected.status !== 'CLOSED' && selected.status !== 'CLOSING' && (
                <div className="action-stack">
                  {!selected.assignedAgentId && (
                    <button onClick={handleAssign}>
                      <span>✓</span> Assumir conversa
                    </button>
                  )}

                  {/* Transfer area */}
                  <div className="operation-box">
                    <span className="operation-title">Transferir para setor</span>
                    <select
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value) handleTransfer(e.target.value);
                      }}
                    >
                      <option value="" disabled>Selecione o setor...</option>
                      {allDepartments
                        .filter((d) => d.id !== selected.departmentId)
                        .map((dept) => (
                          <option key={dept.id} value={dept.id}>{dept.name}</option>
                        ))}
                      {allDepartments.length <= 1 && (
                        <option value="" disabled>Nenhum outro setor disponível</option>
                      )}
                    </select>
                  </div>

                  {/* Actions row */}
                  <div className="action-row">
                    <button
                      className="secondary"
                      onClick={() => { setTicketDept(selected.departmentId || ''); setShowTicketModal(true); }}
                      style={{ flex: 1 }}
                      title="Criar ticket a partir desta conversa"
                    >
                      🎫 Ticket
                    </button>
                    <button
                      className="secondary"
                      onClick={handleLoadSgp360}
                      disabled={sgpLoading}
                      style={{ flex: 1 }}
                      title="Carregar dados SGP 360° do cliente"
                    >
                      {sgpLoading ? '🔄' : '🔍'} SGP 360°
                    </button>
                    <button
                      className="secondary"
                      onClick={() => setShowSgpPanel(true)}
                      style={{ flex: 1 }}
                      title="Operações SGP: financeiro, técnico, RADIUS"
                    >
                      🔌 SGP Ops
                    </button>
                  </div>

                  <div className="operation-box">
                    <span className="operation-title">Nota interna</span>
                    <form onSubmit={handleAddNote} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
                      <textarea
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Ex.: Cliente pediu retorno amanhã às 10h..."
                        rows={2}
                        style={{ fontSize: '0.786rem', minHeight: 48, resize: 'none' }}
                      />
                      <button type="submit" className="secondary" disabled={!noteText.trim()} style={{ fontSize: '0.786rem' }}>
                        Registrar
                      </button>
                    </form>
                  </div>

                  <button className="danger" onClick={handleClose}>
                    Encerrar atendimento
                  </button>
                </div>
              )}

              {selected.status === 'CLOSED' && (
                <div className="action-stack">
                  <span className="text-sm text-secondary text-center">
                    Atendimento encerrado em {new Date(selected.updatedAt).toLocaleString('pt-BR')}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ═══════ URL MODAL ═══════ */}
      {showLeadModal && leadForm && leadApplication && (
        <div className="modal-overlay" onClick={() => setShowLeadModal(false)}>
          <form className="modal-content modal-wide" onSubmit={handleSaveLead} onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <div><h2>Pré-cadastro do novo cliente</h2><small>{leadApplication.plan.label || 'Plano não selecionado'} · SGP {leadApplication.plan.sgpPlanId || 'sem código'}</small></div>
              <button type="button" className="modal-close" onClick={() => setShowLeadModal(false)}>×</button>
            </div>
            <div className="modal-body lead-application-form">
              <div className="lead-form-grid">
                <label>Nome completo<input required value={leadForm.name} onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })} /></label>
                <label>CPF ou CNPJ<input required inputMode="numeric" value={leadForm.document} onChange={(e) => setLeadForm({ ...leadForm, document: e.target.value })} /></label>
                <label>Telefone com DDD<input required inputMode="tel" value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} /></label>
                <label>E-mail<input type="email" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} /></label>
                <label className="span-2">Rua<input required value={leadForm.addressStreet} onChange={(e) => setLeadForm({ ...leadForm, addressStreet: e.target.value })} /></label>
                <label>Número<input required value={leadForm.addressNumber} onChange={(e) => setLeadForm({ ...leadForm, addressNumber: e.target.value })} /></label>
                <label>Complemento<input value={leadForm.addressComplement} onChange={(e) => setLeadForm({ ...leadForm, addressComplement: e.target.value })} /></label>
                <label>Bairro<input required value={leadForm.addressDistrict} onChange={(e) => setLeadForm({ ...leadForm, addressDistrict: e.target.value })} /></label>
                <label>Cidade<input required value={leadForm.addressCity} onChange={(e) => setLeadForm({ ...leadForm, addressCity: e.target.value })} /></label>
                <label>UF<input required maxLength={2} value={leadForm.addressState} onChange={(e) => setLeadForm({ ...leadForm, addressState: e.target.value.toUpperCase() })} /></label>
                <label>CEP<input required inputMode="numeric" value={leadForm.addressZipCode} onChange={(e) => setLeadForm({ ...leadForm, addressZipCode: e.target.value })} /></label>
                <label className="span-2">Referência<input value={leadForm.addressReference} onChange={(e) => setLeadForm({ ...leadForm, addressReference: e.target.value })} /></label>
              </div>
              <div className="lead-review-warning">Salvar confirma a revisão humana. Nenhum cadastro é enviado ao SGP nesta ação.</div>
              <div className="flex gap-sm">
                <button type="submit" disabled={leadSaving}>{leadSaving ? 'Salvando...' : 'Salvar revisão'}</button>
                <button type="button" className="secondary" onClick={() => setShowLeadModal(false)}>Cancelar</button>
              </div>
              {leadApplication.status === 'REVIEWED' && leadApplication.sgp?.status !== 'SENT' && (
                <div className="sgp-lead-send-box">
                  <div><strong>Envio supervisionado ao SGP</strong><p>Utiliza a confirmação registrada na conversa e gera auditoria vinculada ao atendimento.</p></div>
                  <div className="lead-form-grid">
                    <label>Vencimento
                      <select required value={sgpLeadForm.dueDateId} onChange={(e) => setSgpLeadForm({ ...sgpLeadForm, dueDateId: e.target.value })}>
                        <option value="">Selecione...</option>
                        {(sgpLeadOptions?.dueDates || []).map((item) => <option key={item.id} value={item.id}>{item.label || item.id}</option>)}
                      </select>
                    </label>
                    <label>Forma de cobrança
                      <select value={sgpLeadForm.billingMethodId} onChange={(e) => setSgpLeadForm({ ...sgpLeadForm, billingMethodId: e.target.value })}>
                        {(sgpLeadOptions?.billingMethods || [{ id: '1', label: 'Boleto' }]).map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                      </select>
                    </label>
                    <label>Vendedor
                      <select value={sgpLeadForm.sellerId} onChange={(e) => setSgpLeadForm({ ...sgpLeadForm, sellerId: e.target.value })}>
                        <option value="">Não informado</option>
                        {(sgpLeadOptions?.sellers || []).map((item) => <option key={item.id} value={item.id}>{item.label || item.id}</option>)}
                      </select>
                    </label>
                    <label>Data de nascimento (se exigida)<input placeholder="DD/MM/AAAA" value={sgpLeadForm.birthDate} onChange={(e) => setSgpLeadForm({ ...sgpLeadForm, birthDate: e.target.value })} /></label>
                  </div>
                  <button type="button" className="danger" disabled={leadSending || !sgpLeadForm.dueDateId} onClick={handleSendLeadToSgp}>
                    {leadSending ? 'Enviando ao SGP...' : 'Confirmar e enviar ao SGP'}
                  </button>
                </div>
              )}
              {leadApplication.sgp?.status === 'SENT' && <div className="lead-review-warning">Pré-cadastro já enviado ao SGP. Protocolo/ID: {leadApplication.sgp.externalId || 'registrado'}</div>}
            </div>
          </form>
        </div>
      )}

      {showUrlModal && (
        <div className="modal-overlay" onClick={() => setShowUrlModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Enviar documento por URL</h2>
              <button type="button" className="modal-close" onClick={() => setShowUrlModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <label>
                URL do documento
                <input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="https://exemplo.com/boleto.pdf" autoFocus />
              </label>
              <label>
                Nome do arquivo (opcional)
                <input value={urlFileName} onChange={(e) => setUrlFileName(e.target.value)} placeholder="boleto.pdf" />
              </label>
              <label>
                Legenda (opcional)
                <input value={urlCaption} onChange={(e) => setUrlCaption(e.target.value)} placeholder="Segunda via do boleto" />
              </label>
              <div className="flex gap-sm">
                <button onClick={handleSendDocumentUrl} disabled={!urlInput.trim() || uploading}>
                  {uploading ? 'Enviando...' : 'Enviar'}
                </button>
                <button className="secondary" onClick={() => setShowUrlModal(false)} type="button">Cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════ SGP 360 MODAL ═══════ */}
      {showSgpModal && sgp360 && (
        <div className="modal-overlay" onClick={() => setShowSgpModal(false)}>
          <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📊 SGP 360° — {selected?.customer?.name || ''}</h2>
              <button type="button" className="modal-close" onClick={() => setShowSgpModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {(() => {
                const sgpData = (sgp360 as any).data || sgp360;
                const customerInfo = sgpData.customer;
                const contractList = sgpData.contracts || [];
                const hasCustomer = !!customerInfo;
                const hasContracts = contractList.length > 0;
                return (
                  <>
                    {/* Dados do cliente SGP */}
                    {hasCustomer && (
                      <div className="sgp-section">
                        <h3>👤 Dados do Cliente (SGP)</h3>
                        <div className="sgp-cards">
                          <div className="sgp-card" style={{ borderLeft: '3px solid var(--color-primary, #0d6efd)' }}>
                            <div className="sgp-card-row"><span>Nome</span><strong>{customerInfo.name || '—'}</strong></div>
                            <div className="sgp-card-row"><span>CPF/CNPJ</span><span>{customerInfo.document || '—'}</span></div>
                            <div className="sgp-card-row"><span>Nascimento</span><span>{customerInfo.birthDate || '—'}</span></div>
                            {customerInfo.phones?.length > 0 && (
                              <div className="sgp-card-row"><span>Telefones</span><span>{customerInfo.phones.join(', ')}</span></div>
                            )}
                            {customerInfo.emails?.length > 0 && (
                              <div className="sgp-card-row"><span>E-mails</span><span style={{ fontSize: '0.786rem' }}>{customerInfo.emails.join(', ')}</span></div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Contratos */}
                    {hasContracts && (
                      <div className="sgp-section">
                        <h3>📄 Contratos ({contractList.length})</h3>
                        <div className="sgp-cards">
                          {contractList.map((item: any, i: number) => {
                            const ct = item.contract || item;
                            const access = item.access;
                            const occurrences = item.occurrences || [];
                            return (
                              <div key={i} className="sgp-card" style={{ borderLeft: `3px solid ${ct.status === 'Ativo' ? 'var(--color-success, #198754)' : 'var(--color-neutral, #6c757d)'}` }}>
                                <div className="sgp-card-title">{ct.planName || `Contrato #${ct.id}`}</div>
                                <div className="sgp-card-row"><span>ID</span><span className="mono">{ct.id}</span></div>
                                <div className="sgp-card-row"><span>Status</span><span className={`pill ${ct.status === 'Ativo' ? 'success' : 'neutral'}`}>{ct.status || '—'}</span></div>
                                <div className="sgp-card-row"><span>Tipo conexão</span><span>{ct.connectionType || '—'}</span></div>
                                <div className="sgp-card-row"><span>Login</span><span className="mono">{ct.serviceLogin || '—'}</span></div>
                                <div className="sgp-card-row"><span>POP</span><span>{ct.popName || ct.popId || '—'}</span></div>
                                {ct.address && (
                                  <div className="sgp-card-row"><span>Endereço</span><span style={{ fontSize: '0.786rem' }}>{[ct.address.street, ct.address.number, ct.address.neighborhood, ct.address.city, ct.address.state].filter(Boolean).join(', ') || '—'}</span></div>
                                )}
                                {access && (
                                  <div className="sgp-card-row"><span>Acesso</span><span className={`pill ${access.available ? 'success' : 'danger'}`}>{access.available ? 'Disponível' : access.status || 'Indisponível'}</span></div>
                                )}
                                {occurrences.length > 0 && (
                                  <div style={{ marginTop: 6, borderTop: '1px solid var(--border-light)', paddingTop: 6 }}>
                                    <span style={{ fontSize: '0.714rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Ocorrências ({occurrences.length})</span>
                                    {occurrences.slice(0, 3).map((occ: any, j: number) => (
                                      <div key={j} style={{ fontSize: '0.786rem', padding: '3px 0', display: 'flex', justifyContent: 'space-between' }}>
                                        <span>{occ.type || occ.subject || '—'}</span>
                                        <span className={`pill ${occ.status === 'Concluída' ? 'success' : 'neutral'}`} style={{ fontSize: '0.714rem' }}>{occ.status || '—'}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Faturas */}
                    {(() => {
                      const allInvoices = sgpData.invoices || [];
                      if (allInvoices.length === 0) return null;
                      const pendingInvoices = allInvoices.filter((inv: any) => inv.status === 'OPEN' || inv.status === 'OVERDUE');
                      const paidInvoices = allInvoices.filter((inv: any) => inv.status === 'PAID');
                      const visibleInvoices = invoiceFilter === 'pending' ? pendingInvoices : paidInvoices;
                      return (
                        <div className="sgp-section">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                            <h3 style={{ margin: 0 }}>💰 Faturas ({allInvoices.length})</h3>
                            <div style={{ display: 'flex', gap: 4, background: 'var(--bg-app)', borderRadius: 'var(--radius-sm)', padding: 2 }}>
                              <button
                                className={invoiceFilter === 'pending' ? '' : 'secondary'}
                                onClick={() => setInvoiceFilter('pending')}
                                style={{ fontSize: '0.714rem', padding: '4px 10px' }}
                              >
                                Pendentes ({pendingInvoices.length})
                              </button>
                              <button
                                className={invoiceFilter === 'paid' ? '' : 'secondary'}
                                onClick={() => setInvoiceFilter('paid')}
                                style={{ fontSize: '0.714rem', padding: '4px 10px' }}
                              >
                                Pagos ({paidInvoices.length})
                              </button>
                            </div>
                          </div>
                          {visibleInvoices.length === 0 ? (
                            <p style={{ fontSize: '0.857rem', color: 'var(--text-secondary)' }}>
                              {invoiceFilter === 'pending' ? 'Nenhuma fatura pendente.' : 'Nenhuma fatura paga encontrada.'}
                            </p>
                          ) : (
                            <div className="sgp-cards">
                              {visibleInvoices.slice(0, 10).map((inv: any, i: number) => (
                                <div key={i} className="sgp-card" style={{ borderLeft: `3px solid ${inv.status === 'OVERDUE' ? 'var(--color-danger, #dc3545)' : inv.status === 'PAID' ? 'var(--color-success, #198754)' : 'var(--color-warning, #ffc107)'}` }}>
                                  <div className="sgp-card-row"><span>Valor</span><strong>R$ {Number(inv.amount).toFixed(2)}</strong></div>
                                  <div className="sgp-card-row"><span>Vencimento</span><span>{inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('pt-BR') : '—'}</span></div>
                                  {inv.overdueDays > 0 && (
                                    <div className="sgp-card-row"><span>Atraso</span><span className="pill danger" style={{ fontSize: '0.714rem' }}>{inv.overdueDays} dias</span></div>
                                  )}
                                  <div className="sgp-card-row">
                                    <span>Status</span>
                                    <span className={`pill ${inv.status === 'PAID' ? 'success' : inv.status === 'OVERDUE' ? 'danger' : 'warning'}`}>
                                      {{ OPEN: 'Aberto', OVERDUE: 'Vencido', PAID: 'Pago' }[inv.status as string] || inv.status}
                                    </span>
                                  </div>
                                  {inv.documentNumber && <div className="sgp-card-row"><span>Documento</span><span className="mono" style={{ fontSize: '0.786rem' }}>{inv.documentNumber}</span></div>}
                                  {inv.holder && <div className="sgp-card-row"><span>Carteira</span><span>{inv.holder}</span></div>}
                                  {inv.digitableLine && (
                                    <div className="sgp-card-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                                      <span style={{ color: 'var(--text-secondary)' }}>Linha digitável</span>
                                      <code style={{ fontSize: '0.714rem', wordBreak: 'break-all', background: 'var(--bg-app)', padding: '4px 8px', borderRadius: 'var(--radius-sm)', width: '100%' }}>{inv.digitableLine}</code>
                                    </div>
                                  )}
                                  {inv.paymentUrl && (
                                    <div className="sgp-card-row">
                                      <a href={inv.paymentUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.786rem' }}>Abrir boleto</a>
                                    </div>
                                  )}
                                  {selected?.status !== 'CLOSED' && (
                                    <button
                                      className="secondary"
                                      onClick={() => handleSendInvoiceToCustomer(inv)}
                                      disabled={sendingInvoiceId === inv.id}
                                      style={{ fontSize: '0.786rem', marginTop: 4 }}
                                    >
                                      {sendingInvoiceId === inv.id ? 'Enviando...' : '📤 Enviar para cliente'}
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {!hasCustomer && !hasContracts && (
                      <div className="empty-state">
                        <p>Nenhum dado SGP encontrado para este cliente.</p>
                        <p style={{ fontSize: '0.857rem', color: 'var(--text-secondary)', marginTop: 8 }}>
                          Verifique se o cliente possui CPF/CNPJ cadastrado e se a integração SGP está configurada para esta empresa.
                        </p>
                      </div>
                    )}
                  </>
                );
              })()}

              <div className="flex gap-sm" style={{ marginTop: 8 }}>
                <button className="secondary" onClick={() => setShowSgpModal(false)}>Fechar</button>
                <button className="secondary" onClick={handleLoadSgp360}>Atualizar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════ SGP OPERATIONS PANEL ═══════ */}
      {showSgpPanel && selected?.customer && (
        <SgpOperationsPanel
          customer={selected.customer}
          onClose={() => setShowSgpPanel(false)}
        />
      )}

      {/* ═══════ TICKET CREATION MODAL ═══════ */}
      {showTicketModal && (
        <div className="modal-overlay" onClick={() => setShowTicketModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🎫 Criar ticket</h2>
              <button type="button" className="modal-close" onClick={() => setShowTicketModal(false)}>×</button>
            </div>
            <form className="modal-body" onSubmit={handleCreateTicket}>
              <label>
                Assunto
                <input
                  value={ticketSubject}
                  onChange={(e) => setTicketSubject(e.target.value)}
                  placeholder="Ex.: Problema com conexão"
                  autoFocus
                />
              </label>
              <label>
                Setor
                <select value={ticketDept} onChange={(e) => setTicketDept(e.target.value)}>
                  <option value="" disabled>Selecione o setor...</option>
                  {availableDepartments.map((dept) => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </label>
              <div className="flex gap-sm">
                <button type="submit" disabled={!ticketSubject.trim() || !ticketDept || ticketCreating}>
                  {ticketCreating ? 'Criando...' : 'Criar ticket'}
                </button>
                <button className="secondary" onClick={() => setShowTicketModal(false)} type="button">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
